/**
 * FormWidgetChat — the INLINE chat view the form card morphs into (#1158).
 *
 * Supersedes the #1081/#1155 approach that mounted the entire FLOATING
 * `VoiceWidgetConnected` (draggable, viewport-positioned overlay) inside the form
 * region — which rendered a SECOND full floating widget on a real embed (shimmer,
 * shake-on-hover, jump-on-drag). Per the #1084 mockups the form card must morph
 * IN PLACE into a lightweight inline chat: document flow, inside the Shadow-DOM
 * form region, NO portal / position:fixed / useDraggable / useResizable.
 *
 * ## Transport reuse
 * Drives the conversation with the standalone `useTextChat` hook (the same WS
 * transport the floating widget uses) — NOT the floating widget. `useTextChat`
 * only needs `browserControlWS` + `config`, and it already filters incoming WS
 * frames by its own outgoing `messageId`, so:
 *   • its `onStreamStart`/`onMessage` fire ONLY for THIS view's own greeting/turn;
 *   • a co-hosted floating `data-shiftgears="chat"` embed streaming concurrently
 *     (different messageId) can NEVER invoke our callbacks.
 * That is the F4 fix: the reply-watchdog (owned by FormWidget) is satisfied via
 * our `onReplyStarted` callback, which is inherently messageId-correlated.
 *
 * ## Render reuse
 * Reuses the widget's presentational atoms — `MessagesArea` (thread + streaming
 * streaming markdown + scroll pinning) and `Suggestions` (quick-reply chips) — both pure
 * and theme-token (`var(--widget-*)`) driven. `getThemeStyles(theme)` is spread
 * onto the wrapper so they inherit the Andys theme. The composer is minimal
 * (input + send) styled from FormWidget.css with the same tokens.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, Sparkle as Sparkles } from '@phosphor-icons/react';
import { MessagesArea, getThemeStyles } from '../VoiceWidget';
import { Suggestions } from '../VoiceWidget/Suggestions';
import { ArrowIcon } from '../VoiceWidget/icons';
import useTextChat from '../../hooks/useTextChat';
import { browserControlWS } from '../../services/browserControlWebSocket';
import { getCurrentSessionId, getOrCreateSessionId } from '../../services/sessionManager';
import { config as legacyConfig } from '../../config/runtimeConfig';

let msgSeq = 0;
const genId = (prefix) => `${prefix}-${Date.now()}-${(msgSeq += 1)}`;

/** Human-friendly first user bubble echoing what the visitor submitted. */
const FORM_TYPE_ECHO = {
  contact: 'I just got in touch through the form.',
  enquiry: 'I just sent an enquiry.',
  check_availability: 'I just asked about availability.',
  finance: 'I just submitted a finance enquiry.',
  test_drive: 'I just requested a test drive.',
};

export function buildSubmissionEcho(lead = {}) {
  const message = typeof lead.message === 'string' ? lead.message.trim() : '';
  if (message) return message;
  return FORM_TYPE_ECHO[lead.formType] || 'I just submitted the form.';
}

/**
 * @param {Object} props
 * @param {Object} props.lead - the saved lead (name/phone/email/message/formType…)
 * @param {Object} props.launchContext - frozen `form_submitted` launchContext
 * @param {Object} props.theme - widget theme (from getTheme in FormWidget)
 * @param {string} props.successBanner - persistent "saved as lead" banner copy
 * @param {Function} props.onReplyStarted - called once the agent's OWN reply
 *   begins (messageId-correlated) so FormWidget can disarm its degraded watchdog
 */
export default function FormWidgetChat({
  lead,
  launchContext,
  theme,
  successBanner,
  onReplyStarted,
}) {
  const themeStyles = useMemo(() => getThemeStyles(theme || {}), [theme]);
  const assistantTitle = legacyConfig.WIDGET_TITLE || 'Assistant';

  const [messages, setMessages] = useState(() => [
    { id: 'user-echo', role: 'user', text: buildSubmissionEcho(lead) },
  ]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');

  // Id of the assistant message currently streaming (greeting or a reply turn).
  const streamingIdRef = useRef(null);
  // onReplyStarted must fire at most once per turn; keep the callback fresh
  // without re-subscribing useTextChat.
  const onReplyStartedRef = useRef(onReplyStarted);
  onReplyStartedRef.current = onReplyStarted;

  const markReplyStarted = useCallback(() => {
    onReplyStartedRef.current?.();
  }, []);

  const setStreamingText = useCallback((text, { done = false, dropIfEmpty = false } = {}) => {
    const id = streamingIdRef.current;
    if (!id) return;
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      if (dropIfEmpty && (!text || text.trim().length === 0)) {
        return prev.filter((m) => m.id !== id);
      }
      next[idx] = { ...next[idx], text, isStreaming: !done };
      return next;
    });
    if (done) streamingIdRef.current = null;
  }, []);

  const handleStreamStart = useCallback(() => {
    // Correlated to OUR messageId by useTextChat — a foreign floating-chat stream
    // never reaches here. This is the signal that disarms FormWidget's watchdog.
    markReplyStarted();
  }, [markReplyStarted]);

  const handleMessage = useCallback(
    (_chunk, accumulated) => {
      markReplyStarted();
      setStreamingText(accumulated);
    },
    [markReplyStarted, setStreamingText]
  );

  const handleComplete = useCallback(
    (finalText) => {
      setStreamingText(finalText, { done: true, dropIfEmpty: true });
    },
    [setStreamingText]
  );

  const handleError = useCallback(() => {
    // The agent errored/dropped. Remove the empty placeholder; FormWidget's
    // watchdog (if still armed) drives the degraded confirmation. The lead is
    // already saved, so nothing is lost.
    setStreamingText('', { done: true, dropIfEmpty: true });
  }, [setStreamingText]);

  const handleSuggestions = useCallback((items) => {
    setSuggestions(Array.isArray(items) ? items : []);
  }, []);

  // Resolve the session id ONCE (synchronously) so useTextChat has it before the
  // greeting send; the WS singleton also carries it as a fallback after connect.
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = getCurrentSessionId() || getOrCreateSessionId();
  }

  const { sendMessage } = useTextChat({
    sessionId: sessionIdRef.current,
    inventoryId: legacyConfig.INVENTORY_ID,
    onStreamStart: handleStreamStart,
    onMessage: handleMessage,
    onComplete: handleComplete,
    onError: handleError,
    onSuggestions: handleSuggestions,
  });
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const appendAssistantPlaceholder = useCallback((prefix) => {
    const id = genId(prefix);
    streamingIdRef.current = id;
    setMessages((prev) => [
      ...prev,
      { id, role: 'assistant', text: '', isStreaming: true },
    ]);
    return id;
  }, []);

  // ─── Greeting: send the frozen launchContext EXACTLY ONCE on mount ─────────
  // A per-instance ref is sufficient for exactly-once: React 18 StrictMode
  // double-invokes this effect (setup→cleanup→setup) on the SAME fiber, so the
  // ref set on the first run is preserved and short-circuits the second. On a
  // genuine remount (the degraded "try again" re-morph) a fresh instance gets a
  // fresh ref and correctly greets again.
  const greetedRef = useRef(false);
  useEffect(() => {
    if (greetedRef.current) return;
    greetedRef.current = true;

    appendAssistantPlaceholder('greeting');

    (async () => {
      const sid = sessionIdRef.current;
      try {
        // Shared WS singleton — the in-flight-promise guard dedups a concurrent
        // connect() from a co-hosted floating chat, so this never opens a 2nd
        // socket. No-op if already connected.
        await browserControlWS.connect(legacyConfig.BROWSER_CONTROL_WS_URL, sid);
      } catch (err) {
        console.warn('[FormWidgetChat] WS connect failed — degraded fallback will fire:', err);
        return;
      }
      // Empty message + launchContext = auto-greeting (the agent greets from the
      // frozen `form_submitted` context). useTextChat has already subscribed its
      // WS listeners in its own mount effect by now.
      sendMessageRef.current?.('', { launchContext });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  // ─── Composer / quick-reply → send a real user turn ────────────────────────
  const sendUserTurn = useCallback(
    (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      setSuggestions([]);
      setMessages((prev) => [
        ...prev,
        { id: genId('user'), role: 'user', text: trimmed },
      ]);
      appendAssistantPlaceholder('assistant');
      sendMessageRef.current?.(trimmed);
    },
    [appendAssistantPlaceholder]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const text = input.trim();
      if (!text) return;
      sendUserTurn(text);
      setInput('');
    },
    [input, sendUserTurn]
  );

  return (
    <div className="sg-form-widget sg-form-widget--chat" style={themeStyles}>
      <div className="sg-form-widget__banner" role="status">
        <CheckCircle className="sg-form-widget__banner-icon" aria-hidden="true" />
        <span>{successBanner}</span>
      </div>

      <div className="sg-form-widget__chat">
        <div className="sg-form-widget__chat-head">
          <Sparkles className="sg-form-widget__chat-head-icon" aria-hidden="true" />
          <span className="sg-form-widget__chat-head-title">{assistantTitle}</span>
        </div>

        <MessagesArea
          className="sg-form-widget__thread"
          messages={messages}
          autoScroll
          initialScroll
        />

        <Suggestions
          suggestions={suggestions}
          onSelect={(text) => sendUserTurn(text)}
          maxVisible={legacyConfig.SUGGESTIONS_MAX_VISIBLE || 3}
        />

        <form className="sg-form-widget__composer" onSubmit={handleSubmit}>
          <input
            type="text"
            className="sg-form-widget__composer-input"
            placeholder="Type a message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Type a message"
          />
          <button
            type="submit"
            className="sg-form-widget__composer-send"
            disabled={!input.trim()}
            aria-label="Send message"
          >
            <ArrowIcon size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

FormWidgetChat.propTypes = {
  lead: PropTypes.object,
  launchContext: PropTypes.object,
  theme: PropTypes.object,
  successBanner: PropTypes.string,
  onReplyStarted: PropTypes.func,
};
